import Phaser from 'phaser';

interface MiniMapEntity {
  x: number;       // grid coords (0..gridWidth)
  y: number;
  color: number;
  size?: number;   // pixel radius on the minimap (default 1.5)
}

interface MiniMapData {
  player?: MiniMapEntity;
  enemies: MiniMapEntity[];
  portals: MiniMapEntity[];
  frenzy?: { x: number; y: number; size: number }; // grid coords, size in cells
}

const MAP_SIZE = 140;          // pixels (square)
const MARGIN = 12;
const BG_COLOR = 0x000000;
const BG_ALPHA = 0.55;
const BORDER_COLOR = 0x444466;

export type MiniMapAnchor = 'top-right' | 'bottom-right' | 'top-left' | 'bottom-left';

/**
 * Top-right radar overlay. Pure cosmetic UI — server doesn't know it exists,
 * so adding/removing is agent-safe.
 *
 * Reads game state from broadcasts and draws dots: player (white), enemies
 * (red bots / cyan humans), active portals (green), active frenzy (gold halo).
 *
 * Sized to ~10% of typical viewports; clipped to a square so giant grids stay
 * legible without scaling each entity into invisibility.
 */
export class MiniMap {
  private scene: Phaser.Scene;
  private gridW: number;
  private gridH: number;

  private container: Phaser.GameObjects.Container;
  private bg: Phaser.GameObjects.Rectangle;
  private gfx: Phaser.GameObjects.Graphics;

  constructor(scene: Phaser.Scene, gridW: number, gridH: number, anchor: MiniMapAnchor = 'bottom-right') {
    this.scene = scene;
    this.gridW = gridW;
    this.gridH = gridH;

    const { width, height } = scene.scale;
    const labelGap = 14;
    const totalH = MAP_SIZE + labelGap;
    let x: number;
    let y: number;
    switch (anchor) {
      case 'top-right':    x = width - MAP_SIZE - MARGIN;  y = MARGIN; break;
      case 'top-left':     x = MARGIN;                      y = MARGIN; break;
      case 'bottom-left':  x = MARGIN;                      y = height - totalH - MARGIN; break;
      case 'bottom-right':
      default:             x = width - MAP_SIZE - MARGIN;  y = height - totalH - MARGIN; break;
    }

    this.bg = scene.add.rectangle(MAP_SIZE / 2, MAP_SIZE / 2, MAP_SIZE, MAP_SIZE, BG_COLOR, BG_ALPHA)
      .setStrokeStyle(1, BORDER_COLOR, 0.7);
    this.gfx = scene.add.graphics();

    const label = scene.add.text(MAP_SIZE / 2, MAP_SIZE + 4, 'RADAR', {
      fontFamily: 'monospace', fontSize: '9px', color: '#666677',
    }).setOrigin(0.5, 0);

    this.container = scene.add.container(x, y, [this.bg, this.gfx, label])
      .setScrollFactor(0)
      .setDepth(450);
  }

  /** Draws the full state. Cheap (~50 fillCircle calls/tick at most). */
  draw(data: MiniMapData): void {
    this.gfx.clear();
    const sx = MAP_SIZE / this.gridW;
    const sy = MAP_SIZE / this.gridH;

    // Frenzy zone halo (drawn first, behind dots)
    if (data.frenzy) {
      const fx = data.frenzy.x * sx;
      const fy = data.frenzy.y * sy;
      const fr = Math.max(4, data.frenzy.size * sx);
      this.gfx.fillStyle(0xffd166, 0.25);
      this.gfx.fillCircle(fx, fy, fr);
      this.gfx.lineStyle(1, 0xffd166, 0.7);
      this.gfx.strokeCircle(fx, fy, fr);
    }

    // Portals
    for (const p of data.portals) {
      const px = p.x * sx;
      const py = p.y * sy;
      this.gfx.fillStyle(p.color, 0.9);
      this.gfx.fillCircle(px, py, p.size ?? 3);
      // Pulse ring
      this.gfx.lineStyle(1, p.color, 0.5);
      this.gfx.strokeCircle(px, py, (p.size ?? 3) + 2);
    }

    // Enemies
    for (const e of data.enemies) {
      this.gfx.fillStyle(e.color, 0.9);
      this.gfx.fillCircle(e.x * sx, e.y * sy, e.size ?? 1.8);
    }

    // Player (drawn last, on top, with outline)
    if (data.player) {
      const px = data.player.x * sx;
      const py = data.player.y * sy;
      this.gfx.fillStyle(0xffffff, 1);
      this.gfx.fillCircle(px, py, 3);
      this.gfx.lineStyle(1, 0x000000, 1);
      this.gfx.strokeCircle(px, py, 3);
    }
  }

  /** Update the world dims the minimap maps from. The fixed-size HUD
   *  surface (MAP_SIZE × MAP_SIZE) doesn't change — only the source
   *  coordinate scale shifts so a wider arena draws "zoomed out" inside
   *  the same radar tile. */
  resize(gridW: number, gridH: number): void {
    if (gridW === this.gridW && gridH === this.gridH) return;
    this.gridW = gridW;
    this.gridH = gridH;
    this.gfx.clear(); // next draw() repaints with the new scale
  }

  destroy(): void {
    this.container.destroy(true);
  }
}
